import React, { useCallback, useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Alert, Card, CardBody } from "reactstrap"
import TableContainer from "../../components/Common/TableContainer"
import ConfirmModal from "components/Common/ConfirmModal"
import AddBlacklistModal from "components/Blacklist/AddBlacklistModal"
import { formatDate } from "../../helpers/helper_funcations"
import {
  deleteBlacklistEntry,
  getBlacklist,
} from "helpers/backend-helper"

const typeBadge = (type) => {
  const label = type === "domain" ? "Domain" : "Email"
  const colorClass = type === "domain" ? "bg-warning text-dark" : "bg-danger"
  return <span className={`badge ${colorClass}`}>{label}</span>
}

const BlacklistTable = () => {
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState("")
  const [pagination, setPagination] = useState({ totalPages: 0, total: 0 })
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingDelete, setPendingDelete] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState("")

  const fetchEntries = useCallback(async () => {
    setLoading(true)
    setError("")

    try {
      const res = await getBlacklist({
        page: pageIndex + 1,
        limit: pageSize,
        ...(search ? { search } : {}),
      })
      setEntries(res?.data?.entries || [])
      setPagination(res?.pagination || { totalPages: 0, total: 0 })
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load blacklist entries")
      setEntries([])
      setPagination({ totalPages: 0, total: 0 })
    } finally {
      setLoading(false)
    }
  }, [pageIndex, pageSize, search])

  useEffect(() => {
    fetchEntries()
  }, [fetchEntries])

  useEffect(() => {
    if (!successMessage) return
    const t = setTimeout(() => setSuccessMessage(""), 8000)
    return () => clearTimeout(t)
  }, [successMessage])

  useEffect(() => {
    if (!error) return
    const t = setTimeout(() => setError(""), 8000)
    return () => clearTimeout(t)
  }, [error])

  const onSearch = (value) => {
    setPageIndex(0)
    setSearch(value)
  }

  const onPageChange = (nextPageIndex) => {
    setPageIndex(nextPageIndex)
  }

  const onPageSizeChange = (nextSize) => {
    setPageIndex(0)
    setPageSize(nextSize)
  }

  const openConfirm = (entry) => {
    setPendingDelete(entry)
    setDeleteError("")
    setConfirmOpen(true)
  }

  const handleCancelDelete = () => {
    if (deleteLoading) return
    setConfirmOpen(false)
    setPendingDelete(null)
    setDeleteError("")
  }

  const handleConfirmDelete = async () => {
    if (!pendingDelete?.id) return

    setDeleteLoading(true)
    setDeleteError("")

    try {
      await deleteBlacklistEntry(pendingDelete.id)
      setConfirmOpen(false)
      setPendingDelete(null)
      setSuccessMessage("Blacklist entry removed successfully")
      fetchEntries()
    } catch (err) {
      setDeleteError(err?.response?.data?.message || "Failed to delete blacklist entry")
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleAddSuccess = () => {
    setSuccessMessage("Entry added to blacklist successfully")
    setPageIndex(0)
    fetchEntries()
  }

  const columns = useMemo(
    () => [
      {
        Header: "Type",
        accessor: "type",
        disableFilters: true,
        Cell: ({ value }) => typeBadge(value),
      },
      {
        Header: "Value",
        accessor: "value",
        disableFilters: true,
        Cell: ({ value }) => <code>{value}</code>,
      },
      {
        Header: "Note",
        accessor: "note",
        disableFilters: true,
        Cell: ({ value }) => (
          <span style={{ opacity: value ? 1 : 0.7 }}>{value || "—"}</span>
        ),
      },
      {
        Header: "Added by",
        accessor: row => row?.createdBy?.email || "—",
        id: "createdBy",
        disableFilters: true,
        Cell: ({ value }) => <span>{value || "—"}</span>,
      },
      {
        Header: "Added",
        accessor: "createdAt",
        disableFilters: true,
        Cell: ({ value }) => <span>{value ? formatDate(value) : "—"}</span>,
      },
      {
        Header: "Action",
        disableFilters: true,
        Cell: ({ row }) => (
          <div className="flex-row">
            <button
              type="button"
              className="download-rounded-btn"
              onClick={() => openConfirm(row.original)}
              disabled={deleteLoading}
              title={deleteLoading ? "Deleting…" : "Delete"}
              style={{ border: "none" }}
            >
              <i className="mdi mdi-delete" style={{ color: "white", fontSize: 15 }} />
            </button>
          </div>
        ),
      },
    ],
    [deleteLoading]
  )

  return (
    <React.Fragment>
      <div className="row-spaceBtwn align-items-center mb-2">
        <div className="h2 mb-0 heading">Blacklist</div>
        <button
          type="button"
          className="add-new-user"
          onClick={() => setAddModalOpen(true)}
        >
          + Add to blacklist
        </button>
      </div>

      <Card className="shadow-none mb-0">
        <CardBody style={{ background: "#F8F8FB" }} className="pb-0 px-0 px-sm-4">
          {!!error && (
            <Alert color="danger" className="mb-4">
              {String(error)}
            </Alert>
          )}

          {!!successMessage && (
            <Alert color="success" className="mb-4">
              {String(successMessage)}
            </Alert>
          )}

          <TableContainer
            columns={columns}
            data={entries}
            isGlobalFilter={true}
            isJobListGlobalFilter={false}
            isPagination={true}
            iscustomPageSizeOptions={false}
            customPageSize={10}
            totalPages={pagination.totalPages || 0}
            manual
            pageIndex={pageIndex}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onSearch={onSearch}
            searchValue={search}
            loading={loading}
            pagination="pagination pagination-rounded justify-content-end mb-2"
          />
        </CardBody>

        <AddBlacklistModal
          isOpen={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onSuccess={handleAddSuccess}
        />

        <ConfirmModal
          isOpen={confirmOpen}
          title="Confirm Delete"
          confirmLabel={deleteLoading ? "Deleting…" : "Delete"}
          confirmColor="danger"
          loading={deleteLoading}
          errorMessage={deleteError}
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        >
          <p className="mb-2">
            Are you sure you want to remove{" "}
            <strong>{pendingDelete?.value || "this entry"}</strong> from the blacklist?
          </p>
          <p className="text-danger mb-0">This action cannot be undone.</p>
        </ConfirmModal>
      </Card>
    </React.Fragment>
  )
}

BlacklistTable.propTypes = {
  orders: PropTypes.array,
}

export default BlacklistTable
